'use client';

/**
 * ClientI18nProvider (hydration-safe)
 *
 * Guarantees SSR/CSR match by:
 * - Initializing i18next synchronously (initImmediate: false)
 * - Injecting server-provided resources before first render
 * - Forcing the language on an already-initialized instance (HMR-safe)
 *
 * Works for Server + Client Components using react-i18next hooks.
 */

import { useMemo } from 'react';
import i18next, { type i18n, type InitOptions, type Resource } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { languages } from '@/lib/i18n/i18n';

type InitialResources = Resource; // { [lang]: { [ns]: messages } }

let clientI18n: i18n | null = null;

function ensureBundles(i18n: i18n, lang: string, resources?: InitialResources) {
  if (!resources || !resources[lang]) return;
  const namespaces = Object.keys(resources[lang]);
  for (const ns of namespaces) {
    const bundle = resources[lang][ns];
    // Add or overwrite the bundle before first render
    if (!i18n.hasResourceBundle(lang, ns)) {
      i18n.addResourceBundle(lang, ns, bundle, true, true);
    } else {
      // overwrite to avoid stale HMR bundles
      i18n.removeResourceBundle(lang, ns);
      i18n.addResourceBundle(lang, ns, bundle, true, true);
    }
  }
}

function createOrSyncI18n({
  lang,
  initialResources,
  supportedLangs = languages,
  namespaces = [
    'common',
    'auth',
    'subscription',
    'subscriptionTags',
    'account',
    'productCatalog',
    'backgroundTasks',
  ],
}: {
  lang: string;
  initialResources: InitialResources;
  supportedLangs?: string[];
  namespaces?: string[];
}) {
  if (!clientI18n) {
    clientI18n = i18next.createInstance();
    const opts: InitOptions = {
      lng: lang,
      fallbackLng: 'en', // fallback resources ONLY; does not override `lng`
      supportedLngs: supportedLangs,
      ns: namespaces,
      defaultNS: 'common',
      // Provide resources so init is synchronous and matches SSR
      resources: initialResources,
      interpolation: { escapeValue: false },
      returnNull: false,
      initImmediate: false, // sync init -> first render already translated
      react: { useSuspense: false },
    };

    clientI18n
      .use(
        resourcesToBackend(
          (lng: string, ns: string) =>
            import(`@/lib/i18n/messages/${lng}/${ns}.json`)
        )
      )
      .init(opts);

    return clientI18n;
  }

  // Instance already exists (dev/HMR, client navigation)
  // Ensure it has the new bundles and the correct language BEFORE render.
  ensureBundles(clientI18n, lang, initialResources);

  if (clientI18n.language !== lang) {
    // changeLanguage is synchronous if the needed bundle is already loaded
    clientI18n.changeLanguage(lang);
  }

  return clientI18n;
}

export function ClientI18nProvider({
  lang,
  initialResources,
  children,
  supportedLangs = languages,
  namespaces = [
    'common',
    'auth',
    'subscription',
    'subscriptionTags',
    'account',
    'productCatalog',
    'backgroundTasks',
  ],
}: {
  lang: string;
  initialResources: InitialResources;
  children: React.ReactNode;
  supportedLangs?: string[];
  namespaces?: string[];
}) {
  // Create/sync the i18n instance during render using useMemo
  // This avoids accessing ref.current during render
  const i18n = useMemo(
    () =>
      createOrSyncI18n({ lang, initialResources, supportedLangs, namespaces }),
    [lang, initialResources, supportedLangs, namespaces]
  );

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
